import Styles from './PostFloatingPreview.module.scss'
import { Button } from 'blaze-ui/Button'
import { AI, Copy, Send1, Trash2 } from 'blaze-ui/icons'
import { Text } from 'blaze-ui/Text/Text'
import { useIsChannelPostable } from 'components/PostStatusDropdown/utils'
import dayjs from 'dayjs'
import cx from 'classnames'
import { ReactComponent as PreviewUnavailableIcon } from 'screens/blaze/Home/components/Catalogue/components/FilePreview/previewUnavailable.svg'
import { CSSProperties, forwardRef, useState } from 'react'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useModals } from 'almanac-ui/Modal'
import DeleteModal from 'components/NavRightClickMenu/components/DeleteModal/DeleteModal'
import { useRootStore } from 'stores/RootStore'
import { observer } from 'mobx-react-lite'
import { FileType, HandbookItemType } from 'types'
import { values } from 'mobx'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { ChannelIcon } from 'components/ChannelIcon/ChannelIcon'
import { PostPreviewModal } from 'components/PostPreviewModal/PostPreviewModal'
import { PostableChannelType } from 'components/PostPreviewModal/Types'
import { generatePath, Link, useHistory } from 'react-router-dom'
import { DESIGN_PATH, DOC_PATH } from 'paths'
import { VideoDuration } from 'components/VideoDuration/VideoDuration'
import { CalendarEventData } from '../types'
import { lowerCase } from 'lodash'
import { Tag } from 'common/OptionSelect/Tag'
import { useQueryClient } from 'react-query'
import { removeItemFromCalendarCache } from 'hooks/queries/calendar/utils'
import { LoadingShimmerer } from 'components/LoadingShimmerer/LoadingShimmerer'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ConnectedExternalIntegrationsSelect } from 'screens/Analytics/components/ExternalIntegrationSelect/ConnectedExternalIntegrationsSelect'
import { useUpdateScheduledPost } from 'hooks/queries/scheduledPosts/useUpdateScheduledPost'

export const POST_FLOATING_PREVIEW = 'post-floating-preview'

const STATUS_COLOR: Record<string, string> = {
  Draft: '#B1BECD', // grey-bright
  'Reminder Set': '#FFEFC5', // yellow-light
  Autoscheduled: '#FFD74A', // yellow-bright
  Posted: '#D6FFE6', // green-light
  Failed: '#FFCBCB', // red-light
}

interface Props {
  eventData: CalendarEventData
  className?: string
  style?: CSSProperties
  showCopy?: boolean
  showDelete?: boolean
  onMouseLeave?: () => void
  onClose?: () => void
  onDelete?: () => void
  onIntegrationSelect?: () => void
  onDeleteFail?: () => void
  onOptimisticDelete?: (fileId: string) => void
  withinFloatingPopover?: boolean
}

const PostPreviewContent = ({
  image,
  duration,
  isGenerating,
  content,
  title,
}: {
  image: string | null
  duration?: number
  title?: string
  content?: string
  isGenerating: boolean
}) => {
  if (isGenerating) {
    return (
      <div className={Styles.loader}>
        <LoadingShimmerer>
          <div className={Styles.previewTextContainer}>
            <div className={Styles.emptyPreviewText}>Preview is generating</div>
          </div>
        </LoadingShimmerer>
      </div>
    )
  }

  if (!image && content)
    return (
      <div className={Styles.textPreviewContainer}>
        <div className={Styles.textPreviewContent}>
          <div className={Styles.titlePreview}>{title}</div>
          <div className={Styles.textPreview}>{content}</div>
        </div>
      </div>
    )

  if (!image) {
    return (
      <div className={Styles.emptyPreview}>
        <PreviewUnavailableIcon />
        <div className={Styles.emptyPreviewText}>Preview isn't available</div>
      </div>
    )
  }

  return (
    <div className={Styles.previewContainer}>
      <img src={image} alt="preview" />
      {duration && <VideoDuration duration={duration} />}
    </div>
  )
}

export const PostFloatingPreview = observer(
  forwardRef<HTMLDivElement, Props>(
    (
      {
        eventData,
        className,
        style,
        onMouseLeave,
        showCopy = true,
        showDelete = true,
        onClose,
        onIntegrationSelect,
        onDelete,
        onDeleteFail,
        onOptimisticDelete,
        withinFloatingPopover = false,
      }: Props,
      fRef,
    ) => {
      const { openModal } = useModals()
      const rootStore = useRootStore()
      const history = useHistory()
      const queryClient = useQueryClient()
      const updateScheduledPost = useUpdateScheduledPost({ suppressToast: false })
      const { isChannelPostable, isChannelSocialPostable } = useIsChannelPostable()
      const {
        start,
        status,
        image,
        duration,
        asset_type,
        channel,
        content,
        post_url,
        scheduled_post_id,
        image_url_generating,
        id,
        title,
        external_integration_id,
        scheduled_post_provider,
      } = eventData
      const [selectedExternalIntegrationId, setSelectedExternalIntegrationId] = useState<number | undefined>(
        external_integration_id,
      )

      let postAt = ''
      if (start) {
        postAt =
          status === PostStatusPropertyValue.Posted
            ? dayjs(start).format('MMM D, YYYY')
            : dayjs(start).format('h:mma z')
      }

      const handleDelete = () => {
        onClose?.()
        openModal(DeleteModal, {
          files: [{ id, type: lowerCase(asset_type) as FileType, title: title }],
          onOptimisticDelete: () => onOptimisticDelete?.(id),
          onFail: onDeleteFail,
          archivedOnDelete: true,
          onRequest: () => {
            const treeItem = values(rootStore.handbooks.items).find(
              (item) => item?.type === HandbookItemType.Document && item?.data.document?.key === id,
            )
            if (treeItem) rootStore.handbooks.removeItem(treeItem.id)
            removeItemFromCalendarCache(queryClient, id)
            onDelete?.()
          },
        })
      }

      const handleIntegrationSelect = (externalIntegrationId: number) => {
        if (scheduled_post_id) updateScheduledPost.mutate({ id: scheduled_post_id, params: { externalIntegrationId } })
        setSelectedExternalIntegrationId(externalIntegrationId)
        onIntegrationSelect?.()
      }

      const redirectTo = () => {
        if (asset_type === 'Design') {
          return generatePath(DESIGN_PATH, { key: id })
        } else {
          return generatePath(DOC_PATH, { slug: id })
        }
      }

      const isPostable = channel && isChannelSocialPostable(channel)
      const showSeePost = status === PostStatusPropertyValue.Posted && post_url && channel
      const showPostNow =
        status !== PostStatusPropertyValue.Posted && scheduled_post_id && isPostable && selectedExternalIntegrationId
      const showFooter = showCopy || showDelete || showSeePost || showPostNow

      return (
        <div
          className={cx(Styles.container, className, { [Styles.withinFloatingPopover]: withinFloatingPopover })}
          style={style}
          ref={fRef}
          id={POST_FLOATING_PREVIEW}
          onMouseLeave={onMouseLeave}
          data-testid={`post-floating-preview-${id}`}
        >
          <div className={Styles.content}>
            <Link to={redirectTo}>
              <PostPreviewContent
                image={image}
                content={content}
                title={title}
                isGenerating={!!image_url_generating}
                duration={duration}
              />
            </Link>
            <div className={Styles.post}>
              <Link to={redirectTo}>
                <div className={Styles.header}>
                  <div className={Styles.postAt}>{postAt}</div>
                  {channel && isChannelPostable(channel) && status && (
                    <Tag size="sm" color={STATUS_COLOR[status]}>
                      {status}
                    </Tag>
                  )}
                </div>
                <div className={Styles.text}>
                  {asset_type === 'Design' && !content ? (
                    <div className={cx(Styles.emptyCaptionContainer, { [Styles.noPostAt]: !postAt })}>
                      <span className={Styles.emptyCaptionText}>No caption or post copy</span>
                      <Button
                        variant="tertiary"
                        size="xs"
                        frontIcon={AI}
                        onPress={() => history.push(generatePath(DESIGN_PATH, { key: id }))}
                      >
                        Generate
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className={Styles.title}>
                        <Heading lineClamp={1} level={5}>
                          {title}
                        </Heading>
                      </div>
                      <Text lineClamp={postAt || status ? 3 : 4} variant="secondary" color="var(--dark-80)">
                        {content}
                      </Text>
                    </>
                  )}
                </div>
              </Link>
            </div>
          </div>
          {showFooter && (
            <div className={Styles.footer}>
              <div className={Styles.left}>
                {showDelete && (
                  <Button size="xs" variant="editor" frontIcon={Trash2} onPress={handleDelete}>
                    Delete
                  </Button>
                )}
                {showCopy && (
                  <Button
                    size="xs"
                    variant="editor"
                    frontIcon={Copy}
                    onPress={() => {
                      if (!content) return

                      navigator.clipboard.writeText(content)
                      showToastMessage('Post text copied!', 'success')
                    }}
                  >
                    Copy
                  </Button>
                )}
              </div>
              <div className={Styles.right}>
                {status !== PostStatusPropertyValue.Posted && isChannelPostable(channel) && (
                  <div>
                    <ConnectedExternalIntegrationsSelect
                      variant="ghost"
                      onChange={handleIntegrationSelect}
                      onOpenIntegrations={onClose}
                      selectedExternalIntegrationId={selectedExternalIntegrationId}
                      provider={scheduled_post_provider}
                      data-testid="external-integration-select"
                      label="Connect Integration"
                    />
                  </div>
                )}
                {showSeePost && (
                  <ButtonLink fullWidth variant="editor" size="xs" to={post_url}>
                    <div className={Styles.buttonLink}>
                      <ChannelIcon channel={channel} size={16} />
                      See Post
                    </div>
                  </ButtonLink>
                )}
                {showPostNow && (
                  <Button
                    size="xs"
                    frontIcon={Send1}
                    onPress={() => {
                      if (!channel) return

                      onClose?.()
                      openModal(PostPreviewModal, {
                        fileId: id,
                        scheduledPostId: scheduled_post_id,
                        channel: channel as PostableChannelType,
                        showSuccessToast: false,
                        pollPreviewContentStream: false,
                        target: asset_type === 'Design' ? 'design' : 'text',
                      })
                    }}
                  >
                    Post Now
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      )
    },
  ),
)
